<template>
  <!--
    Этапы задают последовательность критериев сортировки отдельно для
    бюджетного и договорного рейтинга каждой конкурсной группы.
  -->
  <v-card class="ranking-steps" elevation="0">
    <v-card-title class="ranking-steps__header">
      <div>
        <h2 class="ranking-steps__title">Этапы вступительных испытаний</h2>
        <p class="ranking-steps__description">
          Настройте правила сортировки абитуриентов отдельно для бюджета и договора.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="ranking-steps__content">
      <v-alert
        v-if="error"
        class="mb-5"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader type="heading, list-item-two-line, article, article" />
      </div>

      <template v-else>
        <v-alert class="mb-5" type="info" variant="tonal">
          Этапы применяются сверху вниз: следующий критерий используется, когда
          результаты по всем предыдущим критериям совпали.
        </v-alert>

        <v-select
          v-model="selectedDepartmentId"
          class="ranking-steps__department-filter"
          :items="departmentOptions"
          item-title="name"
          item-value="id"
          label="Филиал"
          variant="outlined"
          hide-details
        />

        <v-alert
          v-if="!departments.length"
          type="info"
          variant="tonal"
        >
          Нет конкурсных групп с настроенными бюджетными или договорными местами.
        </v-alert>

        <div v-else class="ranking-steps__departments">
          <section
            v-for="department in displayedDepartments"
            :key="department.id"
            class="ranking-steps__department"
          >
            <div
              v-if="isAllDepartments"
              class="ranking-steps__department-heading"
            >
              <h3>{{ department.name }}</h3>
              <v-chip size="small" variant="tonal">
                Конкурсов: {{ department.competitions.length }}
              </v-chip>
            </div>

            <v-expansion-panels
              v-model="expandedCompetitionKeys"
              multiple
            >
              <v-expansion-panel
                v-for="competition in department.competitions"
                :key="competition.key"
                :value="competition.key"
                class="ranking-steps__competition"
                elevation="0"
              >
                <v-expansion-panel-title :competitivegroup="competition.groupId">
                  <div class="ranking-steps__competition-title">
                    <div class="ranking-steps__competition-name">
                      <strong>
                        {{ competition.specialtyCode }} {{ competition.specialtyName }}
                      </strong>
                      <span>{{ competition.studyName }} · {{ competition.description }}</span>
                    </div>
                    <div class="ranking-steps__competition-chips">
                      <v-chip
                        :color="competition.isBudget ? 'primary' : 'secondary'"
                        size="small"
                        variant="tonal"
                      >
                        {{ competition.fundingName }}: {{ competition.seats }} мест
                      </v-chip>
                      <v-chip size="small" variant="tonal">
                        Этапов: {{ competition.steps.length }}
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-title>

                <v-expansion-panel-text>
                  <div
                    v-if="!competition.steps.length"
                    class="ranking-steps__empty"
                  >
                    Правила сортировки для этого конкурса пока не настроены.
                  </div>

                  <div v-else class="ranking-steps__list">
                    <article
                      v-for="(step, index) in competition.steps"
                      :key="step.id"
                      class="ranking-steps__step"
                      :stepid="step.id"
                    >
                      <div class="ranking-steps__step-number">
                        {{ index + 1 }}
                      </div>

                      <div class="ranking-steps__step-main">
                        <strong>{{ step.typeName }}</strong>
                        <span v-if="formatStepSettings(step)">
                          {{ formatStepSettings(step) }}
                        </span>
                        <a
                          v-if="step.type === 'steps-add' && step.examGroupId"
                          class="ranking-steps__exam-link"
                          :href="additionalTestUrl(step.examGroupId)"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <v-icon icon="mdi-table-edit" size="17" />
                          Редактировать оценки
                        </a>
                        <v-chip
                          v-if="!isSupportedStep(step)"
                          class="mt-2"
                          color="warning"
                          size="x-small"
                          variant="tonal"
                        >
                          Устаревший тип — доступно изменение порядка и удаление
                        </v-chip>
                      </div>

                      <div class="ranking-steps__step-actions">
                        <v-btn
                          icon="mdi-arrow-up"
                          size="small"
                          variant="text"
                          aria-label="Поднять этап"
                          :disabled="index === 0 || reorderingKey === competition.key"
                          @click="moveStep(competition, index, -1)"
                        />
                        <v-btn
                          icon="mdi-arrow-down"
                          size="small"
                          variant="text"
                          aria-label="Опустить этап"
                          :disabled="
                            index === competition.steps.length - 1 ||
                              reorderingKey === competition.key
                          "
                          @click="moveStep(competition, index, 1)"
                        />
                        <v-btn
                          icon="mdi-pencil-outline"
                          size="small"
                          variant="text"
                          aria-label="Редактировать этап"
                          :disabled="!isSupportedStep(step)"
                          @click="openEditor(competition, step)"
                        />
                        <v-btn
                          icon="mdi-delete-outline"
                          size="small"
                          variant="text"
                          color="error"
                          aria-label="Удалить этап"
                          @click="openDeleteDialog(competition, step)"
                        />
                      </div>
                    </article>
                  </div>

                  <div class="ranking-steps__add">
                    <v-select
                      v-model="newStepTypes[competition.key]"
                      :items="stepTypes"
                      item-title="name"
                      item-value="id"
                      label="Новый этап"
                      variant="outlined"
                      density="compact"
                      hide-details
                    />
                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-plus"
                      :disabled="!newStepTypes[competition.key]"
                      @click="
                        openEditor(
                          competition,
                          null,
                          newStepTypes[competition.key]
                        )
                      "
                    >
                      Добавить
                    </v-btn>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </section>
        </div>
      </template>
    </v-card-text>

    <v-dialog v-model="editorDialog" max-width="720" persistent>
      <v-card>
        <v-card-title>
          {{ editedStep.id ? 'Редактирование этапа' : 'Новый этап рейтинга' }}
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <div v-if="editedCompetition" class="ranking-steps__dialog-context">
            <strong>
              {{ editedCompetition.specialtyCode }}
              {{ editedCompetition.specialtyName }}
            </strong>
            <span>
              {{ editedCompetition.studyName }} ·
              {{ editedCompetition.fundingName.toLowerCase() }}
            </span>
          </div>

          <v-select
            v-model="editedStep.type"
            class="mb-5"
            :items="stepTypes"
            item-title="name"
            item-value="id"
            label="Тип этапа"
            variant="outlined"
            :readonly="Boolean(editedStep.id)"
            hide-details="auto"
          />

          <v-text-field
            v-if="editedStep.type === 'steps-avg'"
            v-model="editedStep.settings.minBall"
            type="number"
            min="0"
            max="5"
            step="0.01"
            label="Минимальный проходной балл"
            hint="Укажите 0, если минимального балла нет"
            persistent-hint
            variant="outlined"
          />

          <v-select
            v-else-if="editedStep.type === 'steps-enum'"
            v-model="editedStep.settings.grades"
            :items="gradeOptions"
            label="Допустимые оценки аттестата"
            variant="outlined"
            multiple
            chips
            closable-chips
            hint="Выберите оценки, наличие которых учитывается в рейтинге"
            persistent-hint
          />

          <v-autocomplete
            v-else-if="editedStep.type === 'steps-sum'"
            v-model="editedStep.settings.subjects"
            :items="subjects"
            item-title="name"
            item-value="id"
            label="Предметы аттестата"
            variant="outlined"
            multiple
            chips
            closable-chips
            hint="Будет рассчитано среднее арифметическое по выбранным предметам"
            persistent-hint
          />

          <div
            v-else-if="editedStep.type === 'steps-add'"
            class="ranking-steps__dialog-fields"
          >
            <v-text-field
              v-model.trim="editedStep.settings.subject"
              label="Название испытания"
              hint="Это название отображается в рейтинге"
              persistent-hint
              variant="outlined"
            />
            <v-text-field
              v-model="editedStep.settings.minBall"
              type="number"
              min="0"
              step="0.01"
              label="Минимальный проходной балл"
              hint="Укажите 0, если минимального балла нет"
              persistent-hint
              variant="outlined"
            />
          </div>

          <v-select
            v-else-if="editedStep.type === 'steps-priveleges'"
            v-model="editedStep.settings.subjects"
            :items="privileges"
            item-title="name"
            item-value="id"
            label="Льгота"
            variant="outlined"
            hint="Выберите одну льготу из справочника приемной комиссии"
            persistent-hint
          />

          <v-alert
            v-else-if="editedStep.type"
            type="info"
            variant="tonal"
          >
            Этот этап не имеет дополнительных параметров.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="saving" @click="closeEditor">
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            @click="saveStep"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>Удалить этап рейтинга?</v-card-title>
        <v-card-text>
          <v-alert
            v-if="deleteError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ deleteError }}
          </v-alert>
          <p class="mb-2">
            После удаления остальные этапы будут автоматически перенумерованы.
          </p>
          <strong>{{ selectedStep ? selectedStep.typeName : '' }}</strong>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="closeDeleteDialog">
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="deleteStep"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'EntranceExamStepsSection',
  data() {
    return {
      departments: [],
      stepTypes: [],
      subjects: [],
      privileges: [],
      gradeOptions: [3, 4, 5],
      selectedDepartmentId: 0,
      expandedCompetitionKeys: [],
      lastUsedCompetitionKey: '',
      newStepTypes: {},
      loading: true,
      saving: false,
      deleting: false,
      reorderingKey: '',
      error: '',
      dialogError: '',
      deleteError: '',
      editorDialog: false,
      deleteDialog: false,
            editedCompetition: null,
            selectedStep: null,
      editedStep: {
        id: 0,
        type: '',
        settings: {}
      }
    };
  },
  computed: {
    departmentOptions() {
      return [
        { id: 0, name: 'Все филиалы' },
        ...this.departments.map((department) => ({
          id: Number(department.id),
          name: department.name
        }))
      ];
    },
    displayedDepartments() {
      if (Number(this.selectedDepartmentId) === 0) {
        return this.departments;
      }

      return this.departments.filter(
        (department) =>
          Number(department.id) === Number(this.selectedDepartmentId)
      );
    },
    isAllDepartments() {
      return Number(this.selectedDepartmentId) === 0;
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    createEmptyStep(type = '') {
      return {
        id: 0,
        type,
        settings: this.defaultSettings(type)
      };
    },
    defaultSettings(type) {
      switch (type) {
        case 'steps-avg':
          return { minBall: 0 };
        case 'steps-enum':
          return { grades: [] };
        case 'steps-sum':
          return { subjects: [] };
        case 'steps-add':
          return { subject: '', minBall: 0 };
        case 'steps-priveleges':
          return { subjects: null };
        default:
          return {};
      }
    },
    loadData() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionRankingSteps'
        )
        .then((response) => {
          this.departments = response.data.departments || [];
          this.stepTypes = response.data.stepTypes || [];
          this.subjects = response.data.subjects || [];
          this.privileges = response.data.privileges || [];
          // После сохранения данные перечитываются с сервера, но оператор
          // должен продолжить работу в той же раскрытой конкурсной группе.
          this.restoreExpandedCompetitions();
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось загрузить этапы вступительных испытаний.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openEditor(competition, step = null, type = '') {
      this.dialogError = '';
      this.editedCompetition = competition;
      this.keepCompetitionOpen(competition);

      if (step) {
        this.editedStep = {
          id: Number(step.id),
          type: step.type,
          settings: this.normalizeSettings(step.type, step.settings || {})
        };
      } else {
        this.editedStep = this.createEmptyStep(type);
      }

      this.editorDialog = true;
    },
    closeEditor() {
      this.editorDialog = false;
      this.dialogError = '';
      this.editedCompetition = null;
      this.editedStep = this.createEmptyStep();
    },
    normalizeSettings(type, settings) {
      const normalized = this.defaultSettings(type);

      if (type === 'steps-enum') {
        normalized.grades = this.parseIntegerList(settings.grades);
      } else if (type === 'steps-sum') {
        normalized.subjects = this.parseIntegerList(settings.subjects);
      } else if (type === 'steps-priveleges') {
        normalized.subjects = this.parseIntegerList(settings.subjects)[0] || null;
      } else if (type === 'steps-avg') {
        normalized.minBall = Number(settings.minBall || 0);
      } else if (type === 'steps-add') {
        normalized.subject = settings.subject || '';
        normalized.minBall = Number(settings.minBall || 0);
      }

      return normalized;
    },
    parseIntegerList(value) {
      const values = Array.isArray(value)
        ? value
        : String(value || '').split(',');

      return [...new Set(
        values
          .map((item) => Number(item))
          .filter((item) => Number.isInteger(item) && item > 0)
      )];
    },
    validateEditor() {
      const { type, settings } = this.editedStep;
      if (!type) {
        return 'Выберите тип этапа.';
      }
      if (type === 'steps-avg') {
        const minimum = Number(settings.minBall || 0);
        if (Number.isNaN(minimum) || minimum < 0 || minimum > 5) {
          return 'Минимальный средний балл должен быть числом от 0 до 5.';
        }
      }
      if (type === 'steps-enum' && !settings.grades.length) {
        return 'Выберите хотя бы одну оценку.';
      }
      if (type === 'steps-sum' && !settings.subjects.length) {
        return 'Выберите хотя бы один предмет.';
      }
      if (type === 'steps-add') {
        if (!(settings.subject || '').trim()) {
          return 'Введите название дополнительного испытания.';
        }
        if (Number(settings.minBall || 0) < 0) {
          return 'Минимальный балл не может быть отрицательным.';
        }
      }
      if (type === 'steps-priveleges' && !settings.subjects) {
        return 'Выберите льготу.';
      }

      return '';
    },
    saveStep() {
      const validationError = this.validateEditor();
      if (validationError) {
        this.dialogError = validationError;
        return;
      }

      this.saving = true;
      this.dialogError = '';
      const competition = this.editedCompetition;

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/saveAdmissionRankingStep',
          new URLSearchParams({
            id: String(this.editedStep.id || 0),
            groupId: String(competition.groupId),
            funding: competition.funding,
            type: this.editedStep.type,
            settings: JSON.stringify(this.editedStep.settings)
          })
        )
        .then(() => {
          this.newStepTypes[competition.key] = '';
          this.closeEditor();
          this.loadData();
        })
        .catch((error) => {
          this.dialogError = this.getErrorMessage(
            error,
            'Не удалось сохранить этап рейтинга.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    },
        openDeleteDialog(competition, step) {
            this.selectedStep = step;
      this.keepCompetitionOpen(competition);
      this.deleteError = '';
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deleteError = '';
            this.selectedStep = null;
    },
    deleteStep() {
      if (!this.selectedStep) {
        return;
      }

      this.deleting = true;
      this.deleteError = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/deleteAdmissionRankingStep',
          new URLSearchParams({ id: String(this.selectedStep.id) })
        )
        .then(() => {
          this.closeDeleteDialog();
          this.loadData();
        })
        .catch((error) => {
          this.deleteError = this.getErrorMessage(
            error,
            'Не удалось удалить этап рейтинга.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    moveStep(competition, index, offset) {
      // Локальная перестановка отправляется как полный список id: сервер
      // отклонит его, если состав этапов успел измениться в другой сессии.
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= competition.steps.length) {
        return;
      }

      this.keepCompetitionOpen(competition);
      const reordered = [...competition.steps];
      [reordered[index], reordered[targetIndex]] = [
        reordered[targetIndex],
        reordered[index]
      ];
      this.reorderingKey = competition.key;

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/reorderAdmissionRankingSteps',
          new URLSearchParams({
            groupId: String(competition.groupId),
            funding: competition.funding,
            orderedIds: JSON.stringify(reordered.map((step) => step.id))
          })
        )
        .then(() => {
                    competition.steps = reordered;
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось изменить порядок этапов рейтинга.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.reorderingKey = '';
        });
    },
    keepCompetitionOpen(competition) {
      // Запоминаем последний изменяемый конкурс независимо от фильтра филиала.
      if (!competition || !competition.key) {
        return;
      }

      this.lastUsedCompetitionKey = competition.key;
      if (!this.expandedCompetitionKeys.includes(competition.key)) {
        this.expandedCompetitionKeys = [
          ...this.expandedCompetitionKeys,
          competition.key
        ];
      }
    },
    restoreExpandedCompetitions() {
      const availableKeys = new Set(
        this.departments.flatMap((department) =>
          department.competitions.map((competition) => competition.key)
        )
      );
      const expandedKeys = this.expandedCompetitionKeys.filter((key) =>
        availableKeys.has(key)
      );

      if (
        this.lastUsedCompetitionKey &&
        availableKeys.has(this.lastUsedCompetitionKey) &&
        !expandedKeys.includes(this.lastUsedCompetitionKey)
      ) {
        expandedKeys.push(this.lastUsedCompetitionKey);
      }

      this.expandedCompetitionKeys = expandedKeys;
    },
    isSupportedStep(step) {
      return this.stepTypes.some((type) => type.id === step.type);
    },
    formatStepSettings(step) {
      const settings = step.settings || {};

      if (step.type === 'steps-avg') {
        return Number(settings.minBall || 0) > 0
          ? `Минимальный балл: ${settings.minBall}`
          : 'Без минимального балла';
      }
      if (step.type === 'steps-enum') {
        return `Оценки: ${this.parseIntegerList(settings.grades).join(', ')}`;
      }
      if (step.type === 'steps-sum') {
        return this.dictionaryNames(this.subjects, settings.subjects);
      }
      if (step.type === 'steps-add') {
        const minimum = Number(settings.minBall || 0);
        return minimum > 0
          ? `${settings.subject || ''} · минимальный балл: ${minimum}`
          : `${settings.subject || ''} · без минимального балла`;
      }
      if (step.type === 'steps-priveleges') {
        return this.dictionaryNames(this.privileges, settings.subjects);
      }

      return '';
    },
    dictionaryNames(dictionary, ids) {
      const selectedIds = this.parseIntegerList(ids);
      return selectedIds
        .map((id) => {
          const item = dictionary.find(
            (dictionaryItem) => Number(dictionaryItem.id) === id
          );
          return item ? item.name : `#${id}`;
        })
        .join(', ');
    },
    additionalTestUrl(examGroupId) {
      // Ссылка появляется только после сохранения steps-add, когда сервер
      // создал устойчивую группу ведомости для выставления оценок.
      return (
        this.$model.wwwroot +
        `/blocks/manage_groups/#/admission/additionalTest/${examGroupId}`
      );
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response
        ? error.response.data
        : null;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
      if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
      }

      return fallback;
    }
  }
};
</script>

<style scoped>
.ranking-steps {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.ranking-steps__header {
  padding: 24px 28px 14px;
  white-space: normal;
}

.ranking-steps__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.ranking-steps__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.ranking-steps__content {
  padding: 10px 28px 28px;
}

.ranking-steps__department-filter {
  max-width: 420px;
  margin-bottom: 24px;
}

.ranking-steps__departments,
.ranking-steps__department {
  display: grid;
  gap: 18px;
}

.ranking-steps__department-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dfe5ec;
}

.ranking-steps__department-heading h3 {
  margin: 0;
  color: #1d2939;
  font-size: 18px;
}

.ranking-steps__competition {
  border: 1px solid #e4e9f0;
}

.ranking-steps__competition-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 18px;
  padding-right: 12px;
}

.ranking-steps__competition-name {
  display: grid;
  min-width: 0;
  gap: 4px;
}

.ranking-steps__competition-name strong {
  color: #1d2939;
}

.ranking-steps__competition-name span {
  overflow: hidden;
  color: #667085;
  font-size: 13px;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ranking-steps__competition-chips {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.ranking-steps__empty {
  padding: 18px;
  border: 1px dashed #ccd5e0;
  border-radius: 10px;
  color: #667085;
  text-align: center;
}

.ranking-steps__list {
  display: grid;
  gap: 10px;
}

.ranking-steps__step {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e4e9f0;
  border-radius: 10px;
  background: #fff;
}

.ranking-steps__step-number {
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  border-radius: 50%;
  background: #eaf3fc;
  color: #1565c0;
  font-weight: 700;
}

.ranking-steps__step-main {
  display: grid;
  min-width: 0;
  gap: 3px;
}

.ranking-steps__step-main > span {
  color: #667085;
  font-size: 13px;
}

.ranking-steps__exam-link {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  gap: 5px;
  margin-top: 4px;
  color: #1565c0;
  font-size: 13px;
  text-decoration: none;
}

.ranking-steps__exam-link:hover {
  text-decoration: underline;
}

.ranking-steps__step-actions {
  display: flex;
  align-items: center;
}

.ranking-steps__add {
  display: grid;
  grid-template-columns: minmax(260px, 480px) auto;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.ranking-steps__dialog-context {
  display: grid;
  gap: 3px;
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #f5f7fa;
}

.ranking-steps__dialog-context span {
  color: #667085;
  font-size: 13px;
}

.ranking-steps__dialog-fields {
  display: grid;
  gap: 18px;
}

@media (max-width: 800px) {
  .ranking-steps__competition-title,
  .ranking-steps__department-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .ranking-steps__competition-chips {
    flex-wrap: wrap;
  }

  .ranking-steps__step {
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .ranking-steps__step-actions {
    grid-column: 2;
    justify-content: flex-end;
  }

  .ranking-steps__add {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .ranking-steps__header,
  .ranking-steps__content {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
